// ✅ src/pages/VehicleV2/BillingReport.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/billingReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const fmtDateDDMMYYYY = (v) => {
  if (!v) return "";
  const s = toStr(v);
  if (s.includes("/")) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && toStr(v) !== "") return v;
  }
  return fallback;
};

const exportToCsv = (rows, filename = "billing-report.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r[h];
          const s = val === null || val === undefined ? "" : String(val);
          const escaped = s.replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function BillingReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage session ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ backend-matching headers
  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const branchType = sessionBranchType || "BRANCH";
  const branchId = sessionBranchId || "";
  const loginUserId = userId || "";

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // dropdown data
  const [stores, setStores] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);
  const [branches, setBranches] = useState([]);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    vehicleType: "Select",
    branch: branchType === "HO" ? "Select" : String(branchId || ""),
    store: "Select",
    vehicleMainType: "Select",
  });

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ---------------- UI classes ---------------- */
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- load dropdowns ---------------- */
  const loadDropdowns = async () => {
    try {
      setLoading(true);

      const reqs = [
        axios.get(`${API_BASE}/stores`, {
          params: { branchId: branchId || "" },
          headers: apiHeaders,
        }),
        axios.get(`${API_BASE}/vehicleMainTypes`, {
          headers: apiHeaders,
        }),
      ];

      if (branchType === "HO") {
        reqs.push(
          axios.get(`${API_BASE}/branches`, {
            headers: apiHeaders,
          })
        );
      }

      const [storesRes, mainTypesRes, branchesRes] = await Promise.all(reqs);

      setStores(storesRes.data?.rows || []);
      setVehicleMainTypes(mainTypesRes.data?.rows || []);
      setBranches(branchesRes?.data?.rows || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Dropdown load failed"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- load list ---------------- */
  const loadList = async (f = filters) => {
    try {
      setLoading(true);

      const params = {
        branchType,
        branchId,
        loginUserId,
        fromDate: f.fromDate || "",
        toDate: f.toDate || "",
        vehicleType: isSelect(f.vehicleType) ? "" : f.vehicleType,
        branch: isSelect(f.branch) ? "" : f.branch,
        store: isSelect(f.store) ? "" : f.store,
        vehicleMainType: isSelect(f.vehicleMainType)
          ? ""
          : f.vehicleMainType,
      };

      const { data } = await axios.get(`${API_BASE}/list`, {
        params,
        headers: apiHeaders,
      });

      setRows(data?.rows || []);
      setCurrentPage(1);
      toast.success(`Found ${(data?.rows || []).length} records`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "List load failed"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchId || !branchType || !loginUserId) {
      toast.error("Session data missing in localStorage");
      return;
    }

    loadDropdowns();
    loadList(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- events ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleSearch = () => loadList(filters);

  const handleViewAll = () => {
    const reset = {
      fromDate: "",
      toDate: "",
      vehicleType: "Select",
      branch: branchType === "HO" ? "Select" : String(branchId || ""),
      store: "Select",
      vehicleMainType: "Select",
    };
    setFilters(reset);
    loadList(reset);
    toast.info("Showing all records");
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      fromDate: "",
      toDate: "",
      vehicleType: "Select",
      store: "Select",
      vehicleMainType: "Select",
      branch: branchType === "HO" ? "Select" : String(branchId || ""),
    }));
    toast.info("Filters cleared");
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.info("No data to export");
      return;
    }
    exportToCsv(rows, "billing-report.csv");
    toast.success("Exported to CSV");
  };

  const handlePrint = (row) => {
    const billId = pick(
      row,
      ["fld_billid", "BillID", "billId", "fld_billId"],
      0
    );

    if (!billId) {
      toast.error("Bill ID not found in row");
      return;
    }

    navigate("/vehicle/VehicleBillGenerationReport_Print", {
      state: { billId },
    });
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (page) => setCurrentPage(page);

  /* ---------------- columns mapping ---------------- */
  const getBillNo = (r) => pick(r, ["BillNo", "fld_billno", "billNo"], "-");

  const getBranchName = (r) =>
    pick(r, ["BranchName", "fld_branchName", "fld_branchname", "branch"], "-");

  const getVehicleCode = (r) =>
    pick(r, ["fld_vehicle_code", "VehicleCode"], "");

  const getVehicleName = (r) =>
    pick(r, ["fld_vehicle_name", "VehicleName"], "");

  const getVehicleReg = (r) =>
    pick(r, ["fld_vehicle_regno", "VehicleRegNo"], "");

  const getVehicleNumber = (r) =>
    pick(r, ["VehicleNumber", "vehicleNumber"], "") ||
    getVehicleReg(r) ||
    getVehicleCode(r) ||
    "-";

  const getVehicleMainType = (r) =>
    pick(r, ["VehicleType", "fld_vehiclemaintype", "vehicleMainType"], "-");

  const getVehType = (r) =>
    pick(r, ["vehType", "VehicleType", "fld_vehicletypename"], "-");

  const getDate = (r) =>
    fmtDateDDMMYYYY(pick(r, ["Date", "fld_Crdate", "fld_date", "date"], ""));

  const getCompany = (r) =>
    pick(r, ["HireCmp", "CompanyName", "fld_hiredfrom", "companyName"], "-");

  const getAmount = (r) =>
    pick(r, ["AmountToPay", "fld_balance_payable", "amount"], "0.00");

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${
          isDark ? "bg-gray-900" : "bg-gray-100"
        }`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BILL GENERATION REPORT
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-4">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Billing Report
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Type (H/O)</label>
                  <select
                    name="vehicleType"
                    value={filters.vehicleType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    <option value="H">Hired</option>
                    <option value="O">Own</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={branchType !== "HO"}
                  >
                    <option value="Select">Select</option>
                    {branchType === "HO" ? (
                      branches.map((b) => (
                        <option key={b.value} value={b.value}>
                          {b.label}
                        </option>
                      ))
                    ) : (
                      <option value={String(branchId || "")}>
                        Branch {branchId}
                      </option>
                    )}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {stores.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select
                    name="vehicleMainType"
                    value={filters.vehicleMainType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {vehicleMainTypes.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleViewAll}
                    disabled={loading}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                  >
                    <FaEye />
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                  >
                    <FaSearch />
                    {loading ? "Loading..." : "Search Records"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {loading ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  Loading...
                </h3>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Billing Records Found
                </h3>
                <p
                  className={`mb-6 max-w-sm mx-auto ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Try adjusting your search criteria or click "View All" to see
                  all billing records.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">
                        S.No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Bill No
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Number
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Branch
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Date
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Company
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Amount Payable
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => (
                      <tr
                        key={pick(
                          r,
                          ["fld_billid", "BillID", "billId"],
                          `${idx}`
                        )}
                        className={`${
                          isDark ? "bg-gray-800" : "bg-white"
                        } hover:${
                          isDark ? "bg-gray-700" : "bg-gray-50"
                        } transition-colors duration-200`}
                      >
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {indexOfFirstItem + idx + 1}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getVehicleMainType(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getBillNo(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getVehicleNumber(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getBranchName(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getDate(r) || "-"}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {`${getVehicleName(r)} ${
                            getVehicleReg(r) ? `(${getVehicleReg(r)})` : ""
                          }`.trim() || getVehType(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getCompany(r)}
                        </td>

                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm text-right font-medium ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {getAmount(r)}
                        </td>

                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={() => handlePrint(r)}
                            className={`p-2 rounded ${
                              isDark
                                ? "bg-gray-700 text-white hover:bg-gray-600"
                                : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                            }`}
                            title="Print"
                          >
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div
                className={`text-sm mb-4 sm:mb-0 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, rows.length)} of {rows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2)
                    pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    paginate(Math.min(totalPages, currentPage + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}